/*
 * Helpers for the item-based nota de crédito flow (view-order second modal).
 *
 * Tax rates are read from the stamped CFDI XML (never typed by the user):
 * conceptos are grouped by their (IVA, IEPS, ISR) combination and the user
 * only captures one tax-included amount per group. The backend endpoint
 * expects:
 * {
 *   sat_factura_id: number, order_id?: number, motivo?: string,
 *   amounts: [{ amount: number, iva: string, isr: string|null, ieps: string|null }]
 * }
 * where amount is the tax-included total, iva is a whole percent ('0','8','16')
 * or 'exento', and isr/ieps are percent strings or null when absent.
 */

export interface CreditNoteAmountRow
{
	/** 1-based position used in error messages ('monto #N'). */
	index: number;
	/** Tax-included total to credit. */
	amount: number;
	/** IVA from the XML ('8', '16', '0', 'exento'). */
	iva: string;
	/** ISR percent from the XML (null means absent). */
	isr: string | null;
	/** IEPS percent from the XML (null means absent). */
	ieps: string | null;
}

export interface CreditNoteAmountPayload
{
	amount: number;
	iva: string;
	isr: string | null;
	ieps: string | null;
}

/** Tax data of one CFDI concepto, extracted from the XML. */
export interface CfdiConceptoTax
{
	/** Base (Importe attribute). */
	importe: number;
	/** Sum of Traslado.Importe. */
	traslados_importe: number;
	/** Sum of Retencion.Importe. */
	retenciones_importe: number;
	/** 'exento' | percent string, null when the concepto has no IVA traslado. */
	iva: string | null;
	/** IEPS percent (Tasa only), null when absent or Cuota-type. */
	ieps: string | null;
	/** ISR (001) retención percent, null when absent. */
	isr: string | null;
	/** True when the concepto carries IEPS por cuota (not expressible as a rate). */
	has_ieps_cuota: boolean;
	/** True when the concepto has retenciones other than ISR (not supported). */
	has_unsupported_retencion: boolean;
}

/** Conceptos sharing one (IVA, IEPS, ISR) combination. */
export interface CfdiTaxGroup
{
	iva: string;
	ieps: string | null;
	isr: string | null;
	concepto_count: number;
	/** Tax-included total (Importe + traslados − retenciones). */
	total: number;
	has_ieps_cuota: boolean;
}

export interface CfdiTaxGroups
{
	groups: CfdiTaxGroup[];
	ungrouped_count: number;
	ungrouped_total: number;
}

const IVA_RATES = ['0', '8', '16'];
const IVA_EXENTO = ['exento', 'exempt'];

/** '002' = IVA, '003' = IEPS, '001' = ISR. */
const IMPUESTO_IVA = '002';
const IMPUESTO_IEPS = '003';
const IMPUESTO_ISR = '001';

function elementsByLocalName(parent: Element | XMLDocument, local: string): Element[]
{
	let found = parent.getElementsByTagNameNS('*', local);

	if( found.length > 0 )
		return Array.from(found) as Element[];

	return Array.from(parent.getElementsByTagName('cfdi:' + local)) as Element[];
}

function attrNumber(el: Element, name: string): number
{
	let raw = el.getAttribute(name);

	if( raw === null || raw === '' )
		return NaN;

	return parseFloat(raw);
}

/**
 * Converts a CFDI TasaOCuota fraction (0.16) to a backend percent string ('16').
 */
export function fractionToPercentString(fraction: number): string
{
	return String(Math.round(fraction * 10000) / 100);
}

function round2(n: number): number
{
	return Math.round(n * 100) / 100;
}

/**
 * Extracts per-concepto tax data from a CFDI XML string. Throws when the
 * XML cannot be parsed.
 */
export function parseCfdiConceptos(xml: string): CfdiConceptoTax[]
{
	let dom = new DOMParser().parseFromString(xml, 'application/xml');

	if( dom.getElementsByTagName('parsererror').length > 0 )
		throw new Error('El XML de la factura no se pudo leer');

	return elementsByLocalName(dom, 'Concepto').map((el) =>
	{
		let concepto: CfdiConceptoTax =
		{
			importe: attrNumber(el, 'Importe') || 0,
			traslados_importe: 0,
			retenciones_importe: 0,
			iva: null,
			ieps: null,
			isr: null,
			has_ieps_cuota: false,
			has_unsupported_retencion: false
		};

		for(let t of elementsByLocalName(el, 'Traslado'))
		{
			concepto.traslados_importe = round2(concepto.traslados_importe + (attrNumber(t, 'Importe') || 0));

			let impuesto = (t.getAttribute('Impuesto') || '').trim();
			let tipo_factor = (t.getAttribute('TipoFactor') || '').trim().toLowerCase();

			if( impuesto == IMPUESTO_IVA && concepto.iva === null )
			{
				if( tipo_factor == 'exento' )
				{
					concepto.iva = 'exento';
				}
				else
				{
					let tasa = attrNumber(t, 'TasaOCuota');
					concepto.iva = isNaN(tasa) ? null : fractionToPercentString(tasa);
				}
			}
			else if( impuesto == IMPUESTO_IEPS && concepto.ieps === null )
			{
				if( tipo_factor == 'cuota' )
				{
					concepto.has_ieps_cuota = true;
				}
				else
				{
					let tasa = attrNumber(t, 'TasaOCuota');
					concepto.ieps = isNaN(tasa) ? null : fractionToPercentString(tasa);
				}
			}
		}

		for(let r of elementsByLocalName(el, 'Retencion'))
		{
			concepto.retenciones_importe = round2(concepto.retenciones_importe + (attrNumber(r, 'Importe') || 0));

			let impuesto = (r.getAttribute('Impuesto') || '').trim();

			if( impuesto == IMPUESTO_ISR && concepto.isr === null )
			{
				let tasa = attrNumber(r, 'TasaOCuota');
				concepto.isr = isNaN(tasa) ? null : fractionToPercentString(tasa);
			}
			else if( impuesto != IMPUESTO_ISR )
			{
				concepto.has_unsupported_retencion = true;
			}
		}

		return concepto;
	});
}

/**
 * Groups conceptos by (IVA, IEPS, ISR) combination. Conceptos without an IVA
 * traslado or with unsupported retenciones cannot be expressed by the
 * endpoint and are reported as ungrouped instead of dropped silently.
 */
export function groupCfdiConceptsByTax(concepts: CfdiConceptoTax[]): CfdiTaxGroups
{
	let result: CfdiTaxGroups = { groups: [], ungrouped_count: 0, ungrouped_total: 0 };

	for(let c of concepts)
	{
		let line_total = round2(c.importe + c.traslados_importe - c.retenciones_importe);

		if( c.iva === null || c.has_unsupported_retencion )
		{
			result.ungrouped_count++;
			result.ungrouped_total = round2(result.ungrouped_total + line_total);
			continue;
		}

		let key = [c.iva, c.ieps ?? '', c.isr ?? ''].join('|');
		let group = result.groups.find((g)=> [g.iva, g.ieps ?? '', g.isr ?? ''].join('|') == key);

		if( !group )
		{
			group = { iva: c.iva, ieps: c.ieps, isr: c.isr, concepto_count: 0, total: 0, has_ieps_cuota: false };
			result.groups.push(group);
		}

		group.concepto_count++;
		group.total = round2(group.total + line_total);
		group.has_ieps_cuota = group.has_ieps_cuota || c.has_ieps_cuota;
	}

	return result;
}

/** Human label for a group, e.g. 'IVA 16% + IEPS 8% + ISR ret 1.25%'. */
export function formatCfdiTaxGroupLabel(group: CfdiTaxGroup): string
{
	let parts = ['IVA ' + (group.iva == 'exento' ? 'exento' : group.iva + '%')];

	if( group.ieps !== null )
		parts.push('IEPS ' + group.ieps + '%');

	if( group.isr !== null )
		parts.push('ISR ret ' + group.isr + '%');

	return parts.join(' + ');
}

function isExento(value: string): boolean
{
	return IVA_EXENTO.includes(value.trim().toLowerCase());
}

function normalizeRate(value: string | null): string | null
{
	if( value === null || value === undefined )
		return null;

	let trimmed = String(value).trim();

	if( trimmed === '' )
		return null;

	return trimmed;
}

function isZeroRate(value: string | null): boolean
{
	let normalized = normalizeRate(value);

	if( normalized === null )
		return true;

	let num = Number(normalized);

	return !isNaN(num) && num === 0;
}

/**
 * Validates the selected rows. Returns a list of Spanish error messages,
 * each naming its entry ('monto #N ...'). Empty list means valid.
 */
export function validateCreditNoteAmounts(rows: CreditNoteAmountRow[]): string[]
{
	let errors: string[] = [];

	if( rows.length === 0 )
	{
		errors.push('Captura el monto de al menos un grupo de impuestos');
		return errors;
	}

	for(let row of rows)
	{
		let label = 'monto #' + row.index;

		if( row.amount === null || row.amount === undefined || isNaN(Number(row.amount)) || Number(row.amount) <= 0 )
		{
			errors.push(label + ': captura un monto mayor a cero');
			continue;
		}

		let iva = normalizeRate(row.iva);

		if( iva === null )
		{
			errors.push(label + ': captura el IVA (0, 8, 16 o exento)');
		}
		else if( !isExento(iva) && !IVA_RATES.includes(iva) )
		{
			let num = Number(iva);

			if( !isNaN(num) && num > 0 && num < 1 )
				errors.push(label + ': el IVA parece fracción (' + iva + '), usa ' + Math.round(num * 100) + ' en lugar de ' + iva);
			else
				errors.push(label + ': IVA inválido (' + iva + '), usa 0, 8, 16 o exento');
		}

		for(let field of ['isr', 'ieps'] as const)
		{
			let rate = normalizeRate(row[field]);

			if( rate === null )
				continue;

			let num = Number(rate);

			if( isNaN(num) || num < 0 )
				errors.push(label + ': ' + field.toUpperCase() + ' inválido (' + rate + '), usa un porcentaje o déjalo vacío');
		}
	}

	return errors;
}

/**
 * Builds the backend `amounts` payload from validated rows. Zero/empty
 * isr/ieps become null (absent). IVA 'exempt' normalizes to 'exento'.
 */
export function buildCreditNoteAmountsPayload(rows: CreditNoteAmountRow[]): CreditNoteAmountPayload[]
{
	return rows.map((row) =>
	{
		let iva = normalizeRate(row.iva) as string;

		return {
			amount: Math.round(Number(row.amount) * 100) / 100,
			iva: isExento(iva) ? 'exento' : iva,
			isr: isZeroRate(row.isr) ? null : normalizeRate(row.isr),
			ieps: isZeroRate(row.ieps) ? null : normalizeRate(row.ieps)
		};
	});
}
